import React from 'react';
import { Button } from "@/components/ui/button";
import { ArrowLeft, Mail, Phone } from "lucide-react";
import { useNavigate } from 'react-router-dom';
import AppMenu from '../components/AppMenu';
import AppFooter from '../components/AppFooter';

const PageLayout = ({ title, children }) => {
    const navigate = useNavigate();
    return (
        <div className="kh-offer-shell kh-static-page flex flex-col">
            <AppMenu />
            <main className="kh-static-main flex-1">
                <div className="kh-static-inner">
                    <Button variant="outline" onClick={() => navigate(-1)} className="kh-static-back mb-4">
                        <ArrowLeft className="mr-2 h-4 w-4" /> Tillbaka
                    </Button>
                    <h1>{title}</h1>
                    <div className="kh-static-content prose max-w-none leading-relaxed">{children}</div>
                </div>
            </main>
            <AppFooter />
        </div>
    );
};

export const AboutPage = () => (
    <PageLayout title="Om Klart Hemma">
        <p className="font-semibold text-lg">Klart Hemma AB erbjuder snickeri och inredningsmontage med fokus på kök och helhetslösningar i hem. Vi kombinerar tydliga offerter, realistiska tider och hög finish med en kontakt hela vägen.</p>
        <p>Vi hjälper gärna med helheten och samordnar behöriga samarbetspartners inom el och VVS när projektet kräver det.</p>
        
        <h3 className="text-xl font-bold text-primary mt-8 mb-4">Så arbetar vi</h3>
        <ul className="list-disc pl-5 space-y-2">
            <li>Tydligt underlag (bilder + mått) för en trygg prisbild</li>
            <li>Fastpris med spann eller löpande, du väljer</li>
            <li>Kontroll av förutsättningar före start för att minimera överraskningar</li>
            <li>Avstämning och dokumentation vid avslut</li>
        </ul>
    </PageLayout>
);

export const ContactPage = () => (
    <PageLayout title="Kontakta oss">
        <p>Har du frågor eller vill du få hjälp med ett specialprojekt? Hör av dig så återkommer vi så snabbt vi kan.</p>
        
        <div className="my-8 space-y-4 bg-accent-light p-6 rounded-lg border border-accent/20">
            <div className="flex items-center gap-3">
                <Phone className="h-5 w-5 text-accent-dark" />
                <a href="tel:0707388602" className="font-bold hover:underline">0707 388 602</a>
            </div>
            <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-accent-dark" />
                <a href="mailto:kontakt@klarthemma.com" className="font-bold hover:underline">kontakt@klarthemma.com</a>
            </div>
        </div>
        
        <Button size="lg" className="w-full bg-[#374ef4] hover:bg-[#2a3fd4] text-white font-semibold" onClick={() => window.location.href = 'mailto:kontakt@klarthemma.com'}>
            Skicka förfrågan
        </Button>
    </PageLayout>
);

export const TermsPage = () => (
    <PageLayout title="Villkor (sammanfattning)">
        <p>Prisindikation baseras på uppgifter du lämnar i appen. För fastpris krävs komplett underlag.</p>
        <p>Ändringar/tillägg som beställs under projektets gång kan bli ÄTA och debiteras enligt överenskommelse.</p>
        <p>Återbesök på grund av ofärdig arbetszon, saknade leveranser eller ändrade förutsättningar ingår inte om inte annat avtalats.</p>
        <p>För att undvika extra återgångar rekommenderar vi att golv är färdigt innan köksmontage.</p>
        <p>Behöriga arbeten inom el och VVS utförs av behöriga samarbetspartners och omfattning styrs av valt paket.</p>
        <div className="text-xs text-muted-foreground mt-8">Fullständiga villkor bifogas vid offert.</div>
    </PageLayout>
);

export const PrivacyPage = () => (
    <PageLayout title="Integritetspolicy">
        <p>Klart Hemma AB (organisationsnummer 559569-6351), Ostvägen 44, 232 52 Åkarp, ansvarar för de personuppgifter vi behandlar när du använder offertverktyget på offert.klarthemma.com.</p>
        <h2>Uppgifter vi får av dig</h2>
        <p>Vi kan behandla namn, telefonnummer, e-postadress, ort, mått, bilder och din beskrivning av arbetet. Lämna inte känsliga personuppgifter som inte behövs för förfrågan.</p>
        <h2>Varför vi använder uppgifterna</h2>
        <p>Vi använder dem för att ge prisindikation, ta fram offert, kontakta dig och planera eller utföra ett arbete. Behandlingen behövs för att svara på din förfrågan eller fullgöra ett avtal. Uppgifter som omfattas av lagkrav kan behöva sparas längre.</p>
        <p>Om du anmäler dig separat till vårt nyhetsbrev använder vi din e-postadress för att skicka nyheter och erbjudanden. Du kan när som helst återkalla ditt samtycke och avsluta utskicken.</p>
        <h2>Vem som kan få uppgifterna</h2>
        <p>De som hanterar förfrågan hos oss och våra tekniska tjänsteleverantörer kan behandla uppgifterna. Vi kan dela nödvändiga uppgifter med samarbetspartners, till exempel behörig el- eller VVS-utförare, när det krävs för offert eller arbete.</p>
        <h2>Hur länge vi sparar uppgifterna</h2>
        <p>Förfrågningar som inte leder till ett kundprojekt gallras senast 36 månader efter den senaste kontakten när dialogen har upphört. Uppgifter som behövs för ett avtal eller ett lagkrav kan sparas längre. En separat nyhetsbrevsanmälan sparas tills du återkallar samtycket eller avslutar prenumerationen.</p>
        <h2>Dina rättigheter</h2>
        <p>Du kan begära tillgång till, rättelse eller radering av dina uppgifter när reglerna medger det. Du kan också invända mot viss behandling, begära begränsning och lämna klagomål till Integritetsskyddsmyndigheten.</p>
        <p className="mt-4"><strong>Kontakt:</strong> <a href="mailto:kontakt@klarthemma.com">kontakt@klarthemma.com</a></p>
    </PageLayout>
);

export const CookiesPage = () => (
    <PageLayout title="Cookiepolicy">
        <p>Offertverktyget använder lokal lagring för att komma ihåg dina val och din inloggning till admin. Vi har inte aktiverat valfria analys- eller marknadsföringstaggar i verktyget.</p>
        <p>Du kan rensa cookies i din webbläsare.</p>
    </PageLayout>
);

export const UserPolicyPage = () => (
    <PageLayout title="Användarpolicy">
        <p>Genom att använda tjänsten godkänner du att:</p>
        <ul className="list-disc pl-5 space-y-2 mt-4">
            <li>Lämna korrekta uppgifter (mått, bilder, förutsättningar) så att prisindikation blir rättvisande.</li>
            <li>Inte ladda upp olagligt material eller känsliga personuppgifter som inte behövs för ärendet.</li>
            <li>Prisindikation är preliminär tills underlag är komplett och/eller bokad avstämning är genomförd.</li>
        </ul>
        <p className="mt-4">Vid missbruk kan vi neka vidare hantering av ärendet.</p>
    </PageLayout>
);
